import { useMutation, useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import { PAGES_QUERY_KEY } from "@/react/components/SettingsApp/Site/Pages/hooks/usePage";
import { PAGES_QUERY_KEY as PAGES_QUERY_KEY_SITE } from "@/react/components/Site/Pages/hooks/useGetPage";
import { useToast } from "@/react/components/shared/uikit/ToastV2";
import type { ApiError } from "@/react/config/CustomErrors";
import { reactQueryPatch } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers";
import type { Appearance } from "../Store/interfaceSlice";
import type { CustomColors } from "../Store/types";
import type { CssVariables, SiteTheme } from "./constants";
import { THEME_STATS_QUERY_KEY } from "./useGetThemeStats";

interface UpdateSiteThemeData {
  appearance?: Appearance;
  css_variables?: CssVariables;
  custom_colors?: CustomColors;
  id: number;
  page_id?: number;
  set_as_default_theme?: boolean;
  theme_name?: SiteTheme["name"];
}

export const useUpdateSiteTheme = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<
    { theme_name: string; appearance: Appearance },
    ApiError,
    UpdateSiteThemeData
  >({
    mutationFn: ({
      id,
      theme_name,
      appearance,
      css_variables,
      custom_colors,
      page_id = null,
      set_as_default_theme = true,
    }) =>
      reactQueryPatch(
        internalApi.settings.sites.themes.update({ themeId: id }),
        {
          theme_name,
          appearance,
          css_variables,
          custom_colors,
          page_id,
          set_as_default_theme,
        },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries(["settings", "site"]);
      void queryClient.invalidateQueries(PAGES_QUERY_KEY);
      void queryClient.invalidateQueries(PAGES_QUERY_KEY_SITE);
      void queryClient.invalidateQueries(THEME_STATS_QUERY_KEY);
      toast.success(t("changes_saved"));
    },
    onError: error => {
      toast.error(error.message || t("something_went_wrong"));
    },
  });
};
